import { useContext, useEffect, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslations } from '@altermaind/core/i18n';
import { Box, Loader, Typography } from '@altermaind/web/ui';
import { AlertBadge, GoBack, LayoutContext, Tabs } from '@/_shared/components';
import { useTabs } from '@/_shared/hooks/useTabs';
import { TabDepartmentsResultsSurvey, TabOrganizationResultsSurvey } from '../components';
import {
  getSurveyCampaignByIdCall,
  getSurveyCampaignByIdResponsesResultsStatsCall,
} from '../state';

export const SurveyDetailsScreen = () => {
  const t = useTranslations('surveys.details.stats');
  const tShared = useTranslations();

  const { setLayout } = useContext(LayoutContext);
  const { campaignId } = useParams();
  const {
    execute: executeGetSurveyCampaignById,
    loading: loadingGetSurveyCampaignById,
    error: errorGetSurveyCampaignById,
    success: successGetSurveyCampaignById,
  } = getSurveyCampaignByIdCall.hook();

  const { response: responseGetSurveyCampaignByIdResponsesResultsStats } =
    getSurveyCampaignByIdResponsesResultsStatsCall.hook();

  const tabsInitialState = [
    {
      title: t('tabs.tab1'),
      index: 0,
      active: true,
      dataTestId: 'organization',
    },
    {
      title: t('tabs.tab2'),
      index: 1,
      active: false,
      dataTestId: 'departments',
    },
  ];

  const { tabs, setOpenTab } = useTabs({
    tabsInitialState,
  });

  const totalCompleted = useMemo(() => {
    const currentTabActive = tabs.find(tab => tab.active);
    if (currentTabActive?.index === 0 && currentTabActive?.active) {
      return (
        responseGetSurveyCampaignByIdResponsesResultsStats?.organizationStats
          ?.totalCompletedSurveys || 0
      );
    }
    return (
      responseGetSurveyCampaignByIdResponsesResultsStats?.departmentsStats?.[0]
        ?.totalCompletedSurveys ?? 0
    );
  }, [
    responseGetSurveyCampaignByIdResponsesResultsStats?.departmentsStats,
    responseGetSurveyCampaignByIdResponsesResultsStats?.organizationStats?.totalCompletedSurveys,
    tabs,
  ]);

  useEffect(() => {
    if (campaignId) {
      executeGetSurveyCampaignById({
        id: campaignId,
      });
    }
  }, [campaignId, executeGetSurveyCampaignById]);

  useEffect(() => {
    setLayout({ title: 'surveys' });
  }, [setLayout, t]);

  if (loadingGetSurveyCampaignById) {
    return (
      <Box mt='s' textAlign='center'>
        <Loader size='s' />
      </Box>
    );
  }

  if (errorGetSurveyCampaignById) {
    const message = tShared('errors.genericApiError');
    return <AlertBadge containerProps={{ mt: 's' }} iconName='info' type='error' label={message} />;
  }

  if (
    successGetSurveyCampaignById &&
    !loadingGetSurveyCampaignById &&
    !errorGetSurveyCampaignById
  ) {
    return (
      <Box>
        <GoBack />
        <Box mb='s'>
          <Typography mb='xs' variant='h1'>
            {t('title')}
          </Typography>
          <Typography variant='p3/light'>
            {t('description.label', {
              count: totalCompleted,
            })}{' '}
            {totalCompleted === 1 ? t('description.single') : t('description.plural')}
          </Typography>
        </Box>
        <Box>
          <Tabs buttonVariant='primary' setActiveTab={setOpenTab} tabs={tabs} />
          {tabs.map(tab => {
            return (
              <Box key={tab.title} display='flex' flexDirection='column'>
                {tab.index === 0 && tab.active && <TabOrganizationResultsSurvey />}
                {tab.index === 1 && tab.active && <TabDepartmentsResultsSurvey />}
              </Box>
            );
          })}
        </Box>
      </Box>
    );
  }

  return null;
};
